'use client';

import { useFetchGames } from '@shared/hooks';
import { ROUTES, shouldRedirectLotteryToLegacy } from '@shared/utils';
import { useRouter } from 'next/navigation';
import type { GameSearchResult, SearchResult, SearchType } from '../types';

const GAMES_TYPES: SearchType[] = ['games', 'all'];

function useSearchResultNavigation(searchType: SearchType) {
  const router = useRouter();

  const { games: liveCasinoGames, isLoading } = useFetchGames({
    categories: GAMES_TYPES.includes(searchType) ? ['Live Dealer'] : [],
    categoriesMode: 'intersection' as const,
  });

  function buildResultUrl(result: SearchResult): string {
    const { CASINO, LIVE_CASINO, LOTTO, SCRATCHCARDS } = ROUTES;
    const { type, url: path } = result;

    if (type === 'games') {
      const gameName = (result as GameSearchResult).gameName;
      const isLiveCasinoGame = !isLoading && liveCasinoGames.some(game => game.name === gameName);
      if (isLiveCasinoGame) return LIVE_CASINO.GAME(gameName);
      return CASINO.GAME(gameName);
    }
    if (type === 'lotteries') {
      const lotteryUrl = LOTTO.LOTTERY(path);
      if (shouldRedirectLotteryToLegacy(lotteryUrl)) {
        return LOTTO.LOTTERY_LEGACY(path);
      }
      return lotteryUrl;
    }
    if (type === 'syndicates') return LOTTO.SYNDICATE(path);
    if (type === 'scratchcards') return SCRATCHCARDS.SCRATCHCARD(path);

    return path;
  }

  function navigateToResult(result: SearchResult, onNavigate?: () => void) {
    router.push(buildResultUrl(result));
    onNavigate?.();
  }

  return { buildResultUrl, navigateToResult };
}

export { useSearchResultNavigation };
